import { makeAutoObservable } from 'mobx'
import { IntegrationKey, type IIntegrationKey } from 'entities/Integrations'
import { ChatbotBookingType, type IBookingType, type IChatbotSettings } from '../type'

export const DEFAULT_BOOKING_DURATIONS = [15, 30]

export class ChatbotBooking {
  bookingEnabled = false
  type: IBookingType = ChatbotBookingType.TimeChat
  link: IChatbotSettings['book_appointment_link'] = null
  smartBookingEnabled = true
  provider: IIntegrationKey | null = null
  googleCalendar: IChatbotSettings['google_calendar'] = null
  availability: IChatbotSettings['booking_availability'] = null
  durations: number[] | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isGoogleCalendarProvider() {
    return this.provider === IntegrationKey.google_calendar
  }

  setEnabled = (value: boolean) => {
    this.bookingEnabled = value

    if (!value) {
      this.link = null
      this.provider = null
      this.smartBookingEnabled = false
      this.type = ChatbotBookingType.TimeChat
      this.googleCalendar = null
      this.availability = null
      this.durations = null
    }
  }

  setType = (type: IBookingType) => {
    this.type = type
  }

  setLink = (link: IChatbotSettings['book_appointment_link']) => {
    this.link = link
  }

  setProvider = (value: IIntegrationKey | null) => {
    this.provider = value
  }

  setSmartBookingEnabled = (value: boolean) => {
    this.smartBookingEnabled = value
  }

  setGoogleCalendar = (value: IChatbotSettings['google_calendar']) => {
    this.googleCalendar = value
  }

  setAvailability = (value: IChatbotSettings['booking_availability']) => {
    this.availability = value
  }

  setDurations = (value: number[] | null) => {
    this.durations = value
  }

  syncOrigin(settings?: IChatbotSettings | null) {
    this.bookingEnabled = settings?.book_appointment_enabled?.value ?? false
    this.type = settings?.book_appointment_type?.value ?? ChatbotBookingType.TimeChat
    this.link = settings?.book_appointment_link ?? null
    this.smartBookingEnabled = settings?.smart_booking_enabled?.value ?? true
    this.provider = settings?.booking_provider?.value ?? null
    this.googleCalendar = settings?.google_calendar ?? null
    this.availability = settings?.booking_availability ?? null
    this.durations = settings?.booking_durations?.value ?? null
  }

  toJSON(): Pick<
    IChatbotSettings,
    | 'book_appointment_enabled'
    | 'book_appointment_type'
    | 'book_appointment_link'
    | 'smart_booking_enabled'
    | 'booking_provider'
    | 'google_calendar'
    | 'booking_availability'
    | 'booking_durations'
  > {
    return {
      book_appointment_enabled: { value: this.bookingEnabled },
      book_appointment_type: { value: this.type },
      book_appointment_link: this.isGoogleCalendarProvider ? null : this.link,
      smart_booking_enabled: { value: this.smartBookingEnabled },
      booking_provider: this.provider ? { value: this.provider } : null,
      google_calendar: this.isGoogleCalendarProvider ? this.googleCalendar : undefined,
      booking_availability: this.isGoogleCalendarProvider
        ? {
            value: this.availability?.value ?? {},
            timezone: this.availability?.timezone ?? '',
          }
        : undefined,
      booking_durations: this.isGoogleCalendarProvider
        ? { value: this.durations ?? DEFAULT_BOOKING_DURATIONS }
        : undefined,
    }
  }
}
